/** Display formatting in the shop's currency, locale and time zone (UI_UX_GUIDELINES "Money"). */

export interface FormatSettings {
  currencyCode: string;
  currencyDecimals: number;
  locale: string;
  timeZone: string;
}

export const DEFAULT_FORMAT: FormatSettings = {
  currencyCode: 'PKR',
  currencyDecimals: 2,
  locale: 'en-PK',
  timeZone: 'Asia/Karachi',
};

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();
function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat>(key: string, make: () => T): T {
  let f = cache.get(key) as T | undefined;
  if (!f) {
    f = make();
    cache.set(key, f);
  }
  return f;
}

function safeLocale(locale: string): string {
  try {
    return Intl.getCanonicalLocales(locale)[0] ?? 'en';
  } catch {
    return 'en';
  }
}

/** `Rs 1,250.00` — amounts arrive from the API as decimal strings. */
export function money(
  value: string | number | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const f = memo(
    `m:${s.locale}:${s.currencyCode}:${String(s.currencyDecimals)}`,
    () =>
      new Intl.NumberFormat(safeLocale(s.locale), {
        style: 'currency',
        currency: s.currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: s.currencyDecimals,
        maximumFractionDigits: s.currencyDecimals,
      }),
  );
  return f.format(n);
}

/** Compact money for KPI tiles: `Rs 1.2M`. */
export function moneyCompact(
  value: string | number | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const f = memo(
    `mc:${s.locale}:${s.currencyCode}`,
    () =>
      new Intl.NumberFormat(safeLocale(s.locale), {
        style: 'currency',
        currency: s.currencyCode,
        currencyDisplay: 'narrowSymbol',
        notation: 'compact',
        maximumFractionDigits: 1,
      }),
  );
  return f.format(n);
}

/** Quantities: up to 4 decimals, trailing zeros dropped (`12`, `2.5`). */
export function qty(
  value: string | number | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return memo(
    `q:${s.locale}`,
    () => new Intl.NumberFormat(safeLocale(s.locale), { maximumFractionDigits: 4 }),
  ).format(n);
}

/** Business dates (YYYY-MM-DD) are calendar dates: formatted without a time-zone shift. */
export function businessDate(
  value: string | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return value;
  return memo(
    `d:${s.locale}`,
    () =>
      new Intl.DateTimeFormat(safeLocale(s.locale), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }),
  ).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Instants (ISO timestamps) in the shop's time zone. */
export function dateTime(
  value: string | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (!value) return '—';
  return memo(
    `dt:${s.locale}:${s.timeZone}`,
    () =>
      new Intl.DateTimeFormat(safeLocale(s.locale), {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: s.timeZone,
      }),
  ).format(new Date(value));
}

/** "3 min ago", "yesterday" — for notifications and activity. */
export function relativeTime(value: string, now = Date.now()): string {
  const diff = (new Date(value).getTime() - now) / 1000;
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, secs] of units)
    if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  return 'just now';
}

/** Initials for avatars: "Ahmed Builders" → "AB". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase() ||
    '?'
  );
}
